// Dados e helpers da agenda da CEVICO — usados pela Agenda, pelo painel de
// Agendamentos e pelos indicadores do Meu Painel.

export const DOCTORS = [
  { name: 'Dr. Gustavo Bittar', short: 'Gustavo', color: '#0F5FA6' },
  { name: 'Dr. Henrique Gemelli', short: 'Henrique', color: '#B8860B' },
  { name: 'Dra. Roberta Negri', short: 'Roberta', color: '#7C3AED' },
];

// ── 📅 item 210 (23/09): os 4 TIPOS da agenda — cada um com a sua cor ──
// Consultas (avaliação/retorno), Teleconsultas (online), Exames e Cirurgias.
// No banco: cirurgia = task_type 'cirurgia'; os outros três são task_type
// 'consulta' e se separam pela modality ('teleconsulta' | 'exames' | resto).
// Assim o Atendente de Agendamento, o Secretário, a conferência do dia e o
// CRM continuam enxergando tudo como consulta — só a Agenda e os
// Agendamentos mostram cada tipo no seu trilho e na sua cor.
export const ONLINE_UNIT = 'online';
export const KINDS = [
  {
    key: 'consultas',
    label: 'Consultas',
    noun: 'consulta',
    plural: 'consultas',
    article: 'a',
    icon: 'i-lucide-stethoscope',
    color: '#2563EB',
    deep: '#1E40AF',
    grad: 'linear-gradient(135deg, #1D4ED8, #3B82F6)',
    grad2: 'linear-gradient(135deg, #2563EB, #60A5FA)',
    grad3: 'linear-gradient(135deg, #3B82F6, #93C5FD)',
    hero: 'linear-gradient(135deg, #152C61 0%, #1D4ED8 55%, #60A5FA 100%)',
    hint: 'avaliação e retorno nas unidades',
  },
  {
    key: 'teleconsultas',
    label: 'Teleconsultas',
    noun: 'teleconsulta',
    plural: 'teleconsultas',
    article: 'a',
    icon: 'i-lucide-video',
    color: '#7C3AED',
    deep: '#5B21B6',
    grad: 'linear-gradient(135deg, #6D28D9, #A78BFA)',
    grad2: 'linear-gradient(135deg, #7C3AED, #C4B5FD)',
    grad3: 'linear-gradient(135deg, #8B5CF6, #DDD6FE)',
    hero: 'linear-gradient(135deg, #3B0764 0%, #6D28D9 55%, #A78BFA 100%)',
    hint: 'consulta por vídeo, sem unidade',
  },
  {
    key: 'exames',
    label: 'Exames',
    noun: 'exame',
    plural: 'exames',
    article: 'o',
    icon: 'i-lucide-scan-eye',
    color: '#0D9488',
    deep: '#0F766E',
    grad: 'linear-gradient(135deg, #0F766E, #2DD4BF)',
    grad2: 'linear-gradient(135deg, #14B8A6, #5EEAD4)',
    grad3: 'linear-gradient(135deg, #2DD4BF, #99F6E4)',
    hero: 'linear-gradient(135deg, #134E4A 0%, #0F766E 55%, #2DD4BF 100%)',
    hint: 'pentacam, OCT, topografia… nas unidades',
  },
  {
    key: 'cirurgias',
    label: 'Cirurgias',
    noun: 'cirurgia',
    plural: 'cirurgias',
    article: 'a',
    icon: 'i-lucide-slice',
    color: '#0284C7',
    deep: '#075985',
    grad: 'linear-gradient(135deg, #0369A1, #38BDF8)',
    grad2: 'linear-gradient(135deg, #0EA5E9, #7DD3FC)',
    grad3: 'linear-gradient(135deg, #38BDF8, #BAE6FD)',
    hero: 'linear-gradient(135deg, #0C4A6E 0%, #0284C7 55%, #7DD3FC 100%)',
    hint: 'sala cirúrgica das clínicas parceiras',
  },
];
export const KIND_BY_KEY = Object.fromEntries(KINDS.map(k => [k.key, k]));
export const kindFor = key => KIND_BY_KEY[key] || KINDS[0];

// 📅 item 305 (01/10): AGENDAMENTO (consulta, retorno, exame, teleconsulta,
// cirurgia) é da Agenda — nunca cartão do quadro de Tarefas nem do selo do
// menu. A regra é uma só, usada pela Agenda, por Tarefas e pela store.
export const isAppointmentTask = task =>
  !!task &&
  (task.task_type === 'consulta' ||
    task.task_type === 'cirurgia' ||
    !!task.unit);

// a que tipo uma task pertence
export const kindOf = task => {
  if (!task) return 'consultas';
  if (task.task_type === 'cirurgia') return 'cirurgias';
  if (task.modality === 'teleconsulta') return 'teleconsultas';
  if (task.modality === 'exames') return 'exames';
  return 'consultas';
};

// variáveis --cv* do kit "iMac G3 + vidro" para um tipo (a Agenda inteira
// veste a cor do trilho ativo)
export const hexToRgbSpaced = hex => {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return '37 99 235';
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)).join(' ');
};
export const kindVars = kind => {
  const k = typeof kind === 'string' ? kindFor(kind) : kind || KINDS[0];
  return {
    '--cv': k.color,
    '--cv-rgb': hexToRgbSpaced(k.color),
    '--cv-deep': k.deep,
    '--cv-deep-rgb': hexToRgbSpaced(k.deep),
    '--cv-grad': k.grad,
    '--cv-grad-2': k.grad2,
    '--cv-grad-3': k.grad3,
    '--cv-hero': k.hero,
    '--cv-alt': k.color,
    '--cv-alt-rgb': hexToRgbSpaced(k.color),
    '--cv-alt-grad': k.grad,
    '--cv-alt-ink': '#ffffff',
  };
};

// dow: 0=Dom … 6=Sáb; end é exclusivo (último bloco = end - block).
// Mapa padrão — editável em Agenda → "Janelas dos médicos".
export const DEFAULT_WINDOWS = [
  {
    dow: 1,
    unit: 'paulista',
    doctor: 'Dr. Gustavo Bittar',
    turno: 'Manhã',
    start: '08:30',
    end: '10:00',
    block: 15,
  },
  {
    dow: 2,
    unit: 'paulista',
    doctor: 'Dr. Henrique Gemelli',
    turno: 'Manhã',
    start: '08:00',
    end: '11:30',
    block: 15,
  },
  {
    dow: 2,
    unit: 'paulista',
    doctor: 'Dra. Roberta Negri',
    turno: 'Tarde',
    start: '14:30',
    end: '16:30',
    block: 15,
  },
  // item 307 (01/10): quarta 13h–14h do Dr. Henrique = só pós-operatório; consulta a partir das 14h
  {
    dow: 3,
    unit: 'paulista',
    doctor: 'Dr. Henrique Gemelli',
    turno: 'Tarde',
    start: '13:00',
    end: '14:00',
    block: 15,
    only: ['pos_op'],
  },
  {
    dow: 3,
    unit: 'paulista',
    doctor: 'Dr. Henrique Gemelli',
    turno: 'Tarde',
    start: '14:00',
    end: '17:00',
    block: 15,
  },
  {
    dow: 3,
    unit: 'tatuape',
    doctor: 'Dr. Gustavo Bittar',
    turno: 'Manhã',
    start: '08:30',
    end: '11:00',
    block: 10,
  },
  {
    dow: 4,
    unit: 'paulista',
    doctor: 'Dr. Gustavo Bittar',
    turno: 'Manhã',
    start: '08:30',
    end: '11:00',
    block: 15,
  },
  {
    dow: 5,
    unit: 'tatuape',
    doctor: 'Dra. Roberta Negri',
    turno: 'Manhã',
    start: '10:30',
    end: '13:00',
    block: 10,
  },
];

// médicos com a agenda FECHADA (item 76) — as janelas deles somem em
// TODO consumidor (agenda, ocupação, saúde do Meu Painel)
export const resolveClosedDoctors = settings =>
  Array.isArray(settings?.agenda_closed_doctors)
    ? settings.agenda_closed_doctors
    : [];

// TODAS as janelas salvas (ou o padrão), inclusive as de médico com a agenda
// fechada — é o que a tela de Configurações da Agenda edita (item 307)
export const resolveAllWindows = settings => {
  const saved = settings?.agenda_windows;
  return Array.isArray(saved) && saved.length
    ? saved.map(w => ({ ...w, dow: Number(w.dow), block: Number(w.block) }))
    : DEFAULT_WINDOWS;
};

// janelas em vigor — sem os médicos fechados
export const resolveWindows = settings => {
  const list = resolveAllWindows(settings);
  const closed = resolveClosedDoctors(settings);
  return closed.length ? list.filter(w => !closed.includes(w.doctor)) : list;
};

// 📅 ABERTURA PONTUAL (item 375, 28/10 — "a Dra. Roberta pediu para abrir a
// agenda em alguns dias específicos"): um dia (ou um turno a mais) numa DATA
// só, sem mexer na rotina da semana. Mesmo formato da faixa, com `date` no
// lugar do `dow` (o `dow` vem da data, para quem só olha o dia da semana).
// Espelho de Crm::AgendaSlots.open_days — a grade, a ocupação e os robôs leem
// a mesma lista. Cadeados (dia, unidade, médico, horário) valem em cima dela.
const dowOfKey = key => new Date(`${key}T12:00:00`).getDay();
export const resolveOpenDays = settings =>
  Array.isArray(settings?.agenda_open_days)
    ? settings.agenda_open_days
        .filter(
          o =>
            o &&
            /^\d{4}-\d{2}-\d{2}$/.test(o.date) &&
            o.doctor &&
            o.start &&
            o.end
        )
        .map(o => ({
          ...o,
          dow: dowOfKey(o.date),
          block: Number(o.block) || 15,
          open: true,
        }))
    : [];
// aberturas em vigor — sem os médicos com a agenda fechada
export const resolveOpenWindows = settings => {
  const closed = resolveClosedDoctors(settings);
  const list = resolveOpenDays(settings);
  return closed.length ? list.filter(w => !closed.includes(w.doctor)) : list;
};

// 🗂️ item 307 (01/10, "todas as quartas, das 13h às 14h, só retornos de
// pós-operatório"): FAIXA RESERVADA. Cada janela pode aceitar só alguns tipos
// de atendimento (`only`); sem `only` aceita tudo. A IA marca consulta nova
// (avaliação): faixa que não aceita avaliação ela não oferece. Espelho de
// Crm::AgendaSlots (RESERVABLE / accepts?).
export const RESERVABLE = ['avaliacao', 'retorno', 'pos_op'];
export const AI_MODALITY = 'avaliacao';
export const reservedFor = win =>
  Array.isArray(win?.only) ? win.only.filter(k => RESERVABLE.includes(k)) : [];
export const windowAccepts = (win, modality) => {
  const only = reservedFor(win);
  return !only.length || only.includes(modality);
};
const hmToMin = hm => {
  const [h, m] = String(hm || '0:0')
    .split(':')
    .map(Number);
  return h * 60 + (m || 0);
};
// a faixa do médico que cobre aquele horário (mesmo dia da semana e unidade;
// item 375: abertura pontual só na data dela — quem passa `date` enxerga as duas)
export const windowAt = (windows, { dow, unit, minutes, doctor, date }) =>
  windows.find(
    w =>
      w.doctor &&
      (w.date ? w.date === date : w.dow === dow) &&
      w.unit === unit &&
      (!doctor || w.doctor === doctor) &&
      minutes >= hmToMin(w.start) &&
      minutes < hmToMin(w.end)
  );

export const resolveBlocked = settings =>
  Array.isArray(settings?.agenda_blocked) ? settings.agenda_blocked : [];

// janelas da SALA CIRÚRGICA (trilho de cirurgias) — unit recebe a key do
// local para reusar slotsFor/scanAgenda/blockKey sem mudança
// 🔬 AGENDA DE EXAMES (23/09, pedido dele): os exames têm janela PRÓPRIA,
// separada dos médicos — padrão: segunda a sexta, 08h–17h, no IOP da Av.
// Paulista, blocos de 30 min. Editável em Agenda → trilho Exames → "Janela
// de exames" (agenda_config.exam_windows).
export const DEFAULT_EXAM_WINDOWS = [1, 2, 3, 4, 5].map(dow => ({
  dow,
  unit: 'paulista',
  start: '08:00',
  end: '17:00',
  block: 30,
}));
export const resolveExamWindows = settings =>
  Array.isArray(settings?.exam_windows) && settings.exam_windows.length
    ? settings.exam_windows.map(w => ({
        ...w,
        dow: Number(w.dow),
        block: Number(w.block) || 30,
        unit: w.unit || 'paulista',
        exam: true,
      }))
    : DEFAULT_EXAM_WINDOWS.map(w => ({ ...w, exam: true }));

export const resolveSurgeryWindows = settings =>
  Array.isArray(settings?.surgery_windows)
    ? settings.surgery_windows.map(w => ({
        ...w,
        dow: Number(w.dow),
        block: Number(w.block) || 60,
        unit: w.location,
      }))
    : [];

export const resolveBlockedDays = settings =>
  Array.isArray(settings?.agenda_blocked_days)
    ? settings.agenda_blocked_days
    : [];
// item 267: a lista mistura dias INTEIROS ('2026-10-12') e PARTES do dia
// ({ date, unit } ou { date, doctor })
export const wholeBlockedDays = list =>
  new Set(list.filter(b => typeof b === 'string'));
export const partialBlocksOn = (list, key) =>
  list.filter(b => b && typeof b === 'object' && b.date === key);
export const isWindowBlocked = (list, key, win) =>
  partialBlocksOn(list, key).some(
    b =>
      (!b.unit || b.unit === win.unit) && (!b.doctor || b.doctor === win.doctor)
  );
export const sameBlock = (a, b) => {
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  return (
    a.date === b.date &&
    (a.unit || '') === (b.unit || '') &&
    (a.doctor || '') === (b.doctor || '')
  );
};

// blocos ('08:30', '08:45'…) de uma janela
export const slotsFor = win => {
  const [sh, sm] = win.start.split(':').map(Number);
  const [eh, em] = win.end.split(':').map(Number);
  const slots = [];
  for (let t = sh * 60 + sm; t < eh * 60 + em; t += win.block) {
    slots.push(
      `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`
    );
  }
  return slots;
};

export const dateKey = d => {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// item 375: a faixa vale neste dia? abertura pontual = só na data dela;
// faixa da semana = no dia da semana dela
export const windowOnDay = (win, day) =>
  win.date ? win.date === dateKey(day) : win.dow === day.getDay();

export const blockKey = (dateStr, time, unit) => `${dateStr}|${time}|${unit}`;

// 🩺 item 297 (30/09, equipe: "este bloco da janela dos médicos está aparecendo
// diversos pacientes duplicados ocupando agendamentos disponíveis… quando
// apagamos um duplicado todos somem"): na janela do MÉDICO cada agendamento
// ocupa SÓ o bloco em que começa. Antes valia a sobreposição pela duração
// presumida (consulta 15 min, exame 30 min): numa janela de blocos de 10 min a
// mesma consulta aparecia em 2 ou 3 horários e roubava vaga livre.
// Sala cirúrgica e agenda de exames continuam por sobreposição (lá o
// procedimento pode mesmo durar mais que um bloco).
export const occupiesSlot = ({
  taskStart,
  duration,
  slotStart,
  block,
  win,
}) => {
  const slotEnd = slotStart + (Number(block) || 15);
  if (win && win.doctor) return taskStart >= slotStart && taskStart < slotEnd;
  return taskStart < slotEnd && taskStart + duration > slotStart;
};

// modalidades de consulta — alimentam a ocupação por tipo e o formulário.
// item 210: entra 'teleconsulta' (trilho próprio na Agenda, cor violeta).
export const MODALITIES = [
  { key: 'avaliacao', label: 'Avaliação', color: '#2563EB' },
  { key: 'retorno', label: 'Retorno', color: '#D4A017' },
  { key: 'pos_op', label: 'Pós-operatório', color: '#DB2777' }, // item 234
  { key: 'exames', label: 'Exames', color: '#0D9488' },
  { key: 'teleconsulta', label: 'Teleconsulta', color: '#7C3AED' },
];

// 🎨 item 234 (25/09, pedido dele: "cada coisa precisa ter a sua própria cor"
// + "agenda geral" com CAMADAS): os 6 TIPOS de agendamento. Cada tipo tem cor
// própria e vira uma camada que liga/desliga na Agenda; o balão veste a cor
// do TIPO (a unidade virou uma etiqueta pequena). `kind` = trilho antigo que
// o backend e o painel de Agendamentos ainda usam; `resource` = o que o tipo
// ocupa (janela do médico, janela de exames, sala cirúrgica ou nada/online).
const mkType = t => ({
  ...t,
  grad: `linear-gradient(135deg, ${t.deep}, ${t.color})`,
  grad2: `linear-gradient(135deg, ${t.color}, ${t.light})`,
  grad3: `linear-gradient(135deg, ${t.light}, ${t.pale})`,
  hero: `linear-gradient(135deg, ${t.dark} 0%, ${t.deep} 55%, ${t.light} 100%)`,
});
export const TYPES = [
  mkType({
    key: 'avaliacao',
    label: 'Avaliação',
    noun: 'avaliação',
    plural: 'avaliações',
    article: 'a',
    icon: 'i-lucide-stethoscope',
    color: '#2563EB',
    deep: '#1E40AF',
    light: '#60A5FA',
    pale: '#BFDBFE',
    dark: '#172554',
    kind: 'consultas',
    modality: 'avaliacao',
    resource: 'doctor',
    hint: 'primeira consulta com o médico, nas unidades',
  }),
  mkType({
    key: 'retorno',
    label: 'Retorno',
    noun: 'retorno',
    plural: 'retornos',
    article: 'o',
    icon: 'i-lucide-rotate-ccw',
    color: '#D4A017',
    deep: '#A16207',
    light: '#FACC15',
    pale: '#FEF08A',
    dark: '#713F12',
    kind: 'consultas',
    modality: 'retorno',
    resource: 'doctor',
    hint: 'volta ao médico depois de exames ou tratamento',
  }),
  mkType({
    key: 'pos_op',
    label: 'Pós-operatório',
    noun: 'pós-operatório',
    plural: 'pós-operatórios',
    article: 'o',
    icon: 'i-lucide-heart-pulse',
    color: '#DB2777',
    deep: '#9D174D',
    light: '#F472B6',
    pale: '#FBCFE8',
    dark: '#500724',
    kind: 'consultas',
    modality: 'pos_op',
    resource: 'doctor',
    hint: 'revisão depois da cirurgia',
  }),
  mkType({
    key: 'exames',
    label: 'Exame',
    noun: 'exame',
    plural: 'exames',
    article: 'o',
    icon: 'i-lucide-scan-eye',
    color: '#0D9488',
    deep: '#0F766E',
    light: '#2DD4BF',
    pale: '#99F6E4',
    dark: '#134E4A',
    kind: 'exames',
    modality: 'exames',
    resource: 'exam',
    hint: 'pentacam, OCT, topografia… na janela de exames',
  }),
  mkType({
    key: 'teleconsulta',
    label: 'Teleconsulta',
    noun: 'teleconsulta',
    plural: 'teleconsultas',
    article: 'a',
    icon: 'i-lucide-video',
    color: '#7C3AED',
    deep: '#5B21B6',
    light: '#A78BFA',
    pale: '#DDD6FE',
    dark: '#3B0764',
    kind: 'teleconsultas',
    modality: 'teleconsulta',
    resource: 'online',
    hint: 'consulta por vídeo, sem unidade',
  }),
  mkType({
    key: 'cirurgia',
    label: 'Cirurgia',
    noun: 'cirurgia',
    plural: 'cirurgias',
    article: 'a',
    icon: 'i-lucide-slice',
    color: '#EA580C',
    deep: '#9A3412',
    light: '#FB923C',
    pale: '#FED7AA',
    dark: '#431407',
    kind: 'cirurgias',
    modality: null,
    resource: 'surgery',
    hint: 'sala cirúrgica das clínicas parceiras',
  }),
];
export const TYPE_BY_KEY = Object.fromEntries(TYPES.map(t => [t.key, t]));

// 🎨 item 290 (29/09, pedido dele: "parecido com o Google Agenda, mudar as
// cores"): as 12 cores que a equipe pode dar a UM agendamento para se
// organizar. Vale para todos; sem cor = cor padrão do tipo. A lista é a
// MESMA do servidor (Task::COLORS) — cor fora dela o servidor recusa.
export const TASK_COLORS = [
  { value: '#DC2626', label: 'Tomate' },
  { value: '#DB2777', label: 'Pitaya' },
  { value: '#EA580C', label: 'Tangerina' },
  { value: '#EAB308', label: 'Banana' },
  { value: '#84CC16', label: 'Limão' },
  { value: '#16A34A', label: 'Kiwi' },
  { value: '#0D9488', label: 'Hortelã' },
  { value: '#0891B2', label: 'Piscina' },
  { value: '#2563EB', label: 'Mirtilo' },
  { value: '#7C3AED', label: 'Uva' },
  { value: '#86198F', label: 'Ameixa' },
  { value: '#475569', label: 'Grafite' },
];
const TASK_COLOR_SET = new Set(TASK_COLORS.map(c => c.value));
// cor escolhida pela equipe (só se for da lista) ou null
// sinal de presença no balão da grade: ✓ verde (foi) · ✕ vermelho (faltou)
// · ! âmbar (cirurgia: veio e não fez). Só LÊ o dado oficial `attendance`.
export const attendanceMarkOf = task => {
  const surgery = task?.task_type === 'cirurgia';
  if (task?.attendance === 'attended') {
    return {
      sign: '✓',
      cls: 'cv-ag-ev-mark-ok',
      title: surgery ? 'Realizada' : 'Compareceu',
    };
  }
  if (task?.attendance === 'missed') {
    return {
      sign: '✕',
      cls: 'cv-ag-ev-mark-no',
      title: surgery ? 'Não veio' : 'Faltou',
    };
  }
  if (task?.attendance === 'attended_not_done') {
    return { sign: '!', cls: 'cv-ag-ev-mark-warn', title: 'Veio e não fez' };
  }
  return null;
};
// ✅ item 300 (30/09): CONFIRMOU a consulta. O dado oficial é `confirmed_at`
// (SIM ao lembrete, card na coluna "Consulta Confirmada" ou a equipe marcou na
// Agenda). O ✅ que a equipe digitava no nome também vale — e sai do nome, para
// o selo ser um só. `declined_at` = respondeu NÃO ao lembrete.
const TYPE_PREFIX =
  /^(Consulta|Teleconsulta|Exame|Cirurgia|P[oó]s-operat[oó]rio):\s*/i;
const HAND_CHECK = /^\s*✅\s*/;
const bareTitle = task => String(task?.title || '').replace(TYPE_PREFIX, '');
export const patientNameOf = task => bareTitle(task).replace(HAND_CHECK, '');
export const handConfirmed = task => HAND_CHECK.test(bareTitle(task));
export const isConfirmed = task =>
  !task?.canceled_at && (Boolean(task?.confirmed_at) || handConfirmed(task));
export const isDeclined = task =>
  !task?.canceled_at && !isConfirmed(task) && Boolean(task?.declined_at);
export const confirmationTitle = task => {
  if (!isConfirmed(task)) return '';
  if (!task.confirmed_at) return 'Paciente confirmou (marcado pela equipe)';
  const at = new Date(task.confirmed_at);
  const when = `${at.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${at.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  return `Paciente confirmou a consulta em ${when}`;
};
// 🏥 item 300: de quem é o paciente (escolha obrigatória no formulário)
export const ORIGINS = [
  {
    key: 'cevico',
    label: 'CEVICO',
    icon: 'i-lucide-eye',
    color: '#152C61',
    hint: 'Paciente nosso: etiqueta cevico, card no CRM da CEVICO e lembrete pela caixa da CEVICO.',
  },
  {
    key: 'oftalmofacil',
    label: 'Oftalmofácil',
    icon: 'i-lucide-hospital',
    color: '#0D9488',
    hint: 'Paciente do Oftalmofácil: etiqueta of_agenda, card no CRM do Oftalmofácil e lembrete só pela caixa do Oftalmofácil — sem IA.',
  },
];
export const ORIGIN_BY_KEY = Object.fromEntries(ORIGINS.map(o => [o.key, o]));
// 🧭 item 362: COMO O PACIENTE CHEGOU (informado pela equipe; espelho de Crm::LeadChannel)
export const LEAD_CHANNELS = [
  {
    key: 'google_anuncio',
    label: 'Google anúncio',
    title: 'Google · anúncio',
    icon: 'i-lucide-search',
    color: '#0a84ff',
  },
  {
    key: 'google_busca',
    label: 'Google busca/site',
    title: 'Google · busca ou site',
    icon: 'i-lucide-globe',
    color: '#0891b2',
  },
  {
    key: 'meta_anuncio',
    label: 'Insta/Face anúncio',
    title: 'Instagram / Facebook · anúncio',
    icon: 'i-lucide-megaphone',
    color: '#bf5af2',
  },
  {
    key: 'instagram_perfil',
    label: 'Insta perfil/bio',
    title: 'Instagram · perfil ou bio',
    icon: 'i-lucide-instagram',
    color: '#db2777',
  },
  {
    key: 'whatsapp_direto',
    label: 'WhatsApp direto',
    title: 'WhatsApp direto (salvou o número, veio sozinho)',
    icon: 'i-lucide-message-circle',
    color: '#16a34a',
  },
  {
    key: 'ligacao',
    label: 'Ligação',
    title: 'Ligação telefônica',
    icon: 'i-lucide-phone',
    color: '#d97706',
  },
  {
    key: 'indicacao',
    label: 'Indicação',
    title: 'Indicação de paciente',
    icon: 'i-lucide-heart-handshake',
    color: '#dc2626',
  },
  {
    key: 'parceiro',
    label: 'Médico/parceiro',
    title: 'Médico ou parceiro',
    icon: 'i-lucide-stethoscope',
    color: '#0d9488',
  },
  {
    key: 'balcao',
    label: 'Passou aqui',
    title: 'Passou na clínica (balcão)',
    icon: 'i-lucide-building-2',
    color: '#152C61',
  },
  {
    key: 'outro',
    label: 'Outro',
    title: 'Outro',
    icon: 'i-lucide-circle-help',
    color: '#6b7280',
  },
];
export const LEAD_CHANNEL_BY_KEY = Object.fromEntries(
  LEAD_CHANNELS.map(o => [o.key, o])
);
// origem para MOSTRAR: a escolhida no formulário ou, no que veio do hub, o parceiro
export const originOf = task => {
  if (task?.origin && ORIGIN_BY_KEY[task.origin])
    return ORIGIN_BY_KEY[task.origin];
  if (task?.source === 'oftalmofacil' && task.source_detail)
    return ORIGIN_BY_KEY.oftalmofacil;
  return null;
};
export const taskColorOf = task => {
  const hex = String(task?.color || '').toUpperCase();
  return TASK_COLOR_SET.has(hex) ? hex : null;
};
// a "Agenda geral" (todas as camadas ligadas) veste um tom neutro
export const GENERAL_TYPE = mkType({
  key: 'geral',
  label: 'Agenda geral',
  noun: 'agendamento',
  plural: 'agendamentos',
  article: 'o',
  icon: 'i-lucide-layers',
  color: '#475569',
  deep: '#1E293B',
  light: '#94A3B8',
  pale: '#CBD5E1',
  dark: '#0F172A',
  kind: null,
  modality: null,
  resource: null,
  hint: 'todos os tipos juntos — ligue e desligue as camadas',
});
// 🔁 item 368 (23/10, ele: "quando o agente reagenda… fica a mesma cor de retorno; as
// meninas se confundem — precisa ter uma outra corzinha"): REAGENDADA tem cor própria,
// azul-piscina, em toda tela. O âmbar do "Remarcada" era quase o dourado do Retorno.
// Na Agenda a consulta continua com a cor do TIPO (avaliação × retorno muda valor e
// lembrete) e ganha o contorno + o ↻ azul-piscina.
export const RESCHEDULED_COLOR = '#0891B2';
export const isRescheduled = task => Number(task?.rescheduled_count) > 0 && !task?.canceled_at;
export const rescheduledTitle = task => {
  const n = Number(task?.rescheduled_count) || 0;
  return n > 1 ? `Reagendada ${n} vezes` : 'Reagendada';
};
export const typeOf = task => {
  if (!task) return 'avaliacao';
  if (task.task_type === 'cirurgia') return 'cirurgia';
  return TYPE_BY_KEY[task.modality] ? task.modality : 'avaliacao';
};
// ?kind=consultas (links antigos) → camadas
export const LEGACY_KIND_TO_TYPES = {
  consultas: ['avaliacao', 'retorno', 'pos_op'],
  teleconsultas: ['teleconsulta'],
  exames: ['exames'],
  cirurgias: ['cirurgia'],
};
export const modalityFor = key =>
  MODALITIES.find(m => m.key === (key || 'avaliacao')) || MODALITIES[0];

// varre um intervalo de dias e devolve estatísticas + primeiras vagas livres:
// tasks = consultas (com due_at); blockedSet = Set de blockKey.
// byModality: blocos ocupados POR TIPO de consulta (avaliação/retorno/exames)
// 23/09: igual ao servidor (Crm::AgendaSlots) — uma consulta COM unidade só
// ocupa o bloco daquela unidade; sem unidade ocupa em todas. Teleconsultas
// (unidade 'online') não ocupam bloco físico nenhum.
export const scanAgenda = ({
  windows,
  tasks,
  blockedSet,
  blockedDays = new Set(),
  from,
  days,
  freeLimit = 3,
  futureOnly = false,
  pastOnly = false,
}) => {
  const occupied = new Set();
  const modalityByKey = new Map();
  tasks.forEach(t => {
    if (!t.due_at) return;
    if (t.unit === ONLINE_UNIT) return;
    const d = new Date(t.due_at);
    const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    const mod = t.modality || 'avaliacao'; // consulta sem tipo = avaliação
    const key = blockKey(dateKey(d), hm, t.unit || '');
    occupied.add(key);
    if (!modalityByKey.has(key)) modalityByKey.set(key, mod);
  });

  const now = new Date();
  let total = 0;
  let filled = 0;
  const byModality = {};
  const freeSlots = [];

  for (let i = 0; i < days; i += 1) {
    const day = new Date(from);
    day.setDate(day.getDate() + i);
    const dow = day.getDay();
    // fim de semana bloqueado — salvo abertura pontual naquela data (item 375)
    if ((dow === 0 || dow === 6) && !windows.some(w => w.date === dateKey(day))) continue;
    if (blockedDays.has(dateKey(day))) continue; // dia fechado inteiro

    windows
      .filter(w => windowOnDay(w, day))
      .forEach(win => {
        slotsFor(win).forEach(slot => {
          const key = blockKey(dateKey(day), slot, win.unit);
          if (blockedSet.has(key)) return; // fechado com cadeado — fora da conta

          const slotDate = new Date(`${dateKey(day)}T${slot}`);
          if (futureOnly && slotDate <= now) return;
          if (pastOnly && slotDate > now) return;

          total += 1;
          const noUnitKey = blockKey(dateKey(day), slot, '');
          const isTaken = occupied.has(key) || occupied.has(noUnitKey);
          if (isTaken) {
            filled += 1;
            const mod =
              modalityByKey.get(key) ||
              modalityByKey.get(noUnitKey) ||
              'avaliacao';
            byModality[mod] = (byModality[mod] || 0) + 1;
          } else if (freeSlots.length < freeLimit && slotDate > now) {
            freeSlots.push({ day: new Date(day), slot, win });
          }
        });
      });
  }

  return {
    total,
    filled,
    freeSlots,
    byModality,
    pct: total ? Math.round((filled / total) * 100) : 0,
  };
};

// ── 💰 item 324 (05/10): VALOR do agendamento ─────────────────────────
// O servidor (Crm::AppointmentPrice) é quem decide o valor que vai na
// mensagem; aqui é a MESMA regra para o card mostrar antes de salvar:
// lançado à mão → "Valor: X" escrito na observação → "R$ X" escrito no TIPO
// DE CONSULTA (item 340) → pré-configurado do tipo (particular por médico,
// exames, avaliação, retorno, pós-op) → outro "R$ X" na observação → valor
// padrão. 🤖 item 340: o que a IA gravava na observação até a auditoria
// ("Valor da consulta: R$ 150" fixo, bloco "Reagendada pela IA:") não conta.
export const moneyToNumber = text => {
  const t = String(text ?? '')
    .trim()
    .replace(/^R\$\s*/i, '');
  if (/^\d+\.\d{1,2}$/.test(t)) return Number(t);
  if (!/^\d[\d.]*(,\d{1,2})?$/.test(t)) return null;
  return Number(t.replace(/\./g, '').replace(',', '.'));
};
export const formatMoney = n =>
  Number(n).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
// "150" → "R$ 150,00" · "sem custo" → "sem custo"
export const priceLabel = text => {
  const n = moneyToNumber(text);
  return n === null ? String(text ?? '') : `R$ ${formatMoney(n)}`;
};
const foldText = s =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
const sumPrices = values => {
  const numbers = values.map(moneyToNumber).filter(n => n !== null);
  if (!numbers.length) return String(values[0] ?? '');
  return formatMoney(numbers.reduce((a, b) => a + b, 0));
};
const PRICE_TYPES = ['avaliacao', 'retorno', 'pos_op', 'teleconsulta'];
export const PRICE_TYPE_LABELS = {
  avaliacao: 'Avaliação',
  retorno: 'Retorno',
  pos_op: 'Pós-operatório (1º)',
  teleconsulta: 'Teleconsulta',
};
// o pré-configurado do tipo: { text, label } ou null
export const presetAppointmentPrice = (prices, appt) => {
  if (!prices || appt.kind === 'cirurgias') return null;
  if (appt.particular && appt.doctor) {
    const hit = Object.entries(prices.particular || {}).find(
      ([doctor]) => foldText(doctor) === foldText(appt.doctor)
    );
    if (hit && hit[1])
      return { text: hit[1], label: `particular · ${appt.doctor}` };
  }
  if (appt.kind === 'exames' || appt.modality === 'exames') {
    const wanted = foldText(appt.procedure);
    const hits = (prices.exames || []).filter(
      e => e.price && e.name && wanted.includes(foldText(e.name))
    );
    if (!hits.length) return null;
    return {
      text: sumPrices(hits.map(e => e.price)),
      label: hits.map(e => e.name).join(' + '),
    };
  }
  // 🩺 19/10: avaliação com especialista — o problema escrito ("avaliação
  // retina", "glaucoma") puxa o valor do especialista (só na avaliação)
  if (!appt.modality || appt.modality === 'avaliacao') {
    const wanted = foldText(appt.procedure);
    const special = (prices.especiais || []).find(
      e => e.price && e.name && wanted.includes(foldText(e.name))
    );
    if (special)
      return { text: special.price, label: `avaliação de ${special.name}` };
  }
  const type = PRICE_TYPES.includes(appt.modality)
    ? appt.modality
    : 'avaliacao';
  const text = prices[type];
  return text ? { text, label: PRICE_TYPE_LABELS[type].toLowerCase() } : null;
};
// 🤖 item 340: a observação sem as linhas que a IA escrevia nos blocos
// antigos (mesma regra de Crm::AppointmentPrice.human_notes). Num parágrafo
// com o carimbo "— agendado pela IA", tudo até o carimbo é da IA; o bloco
// "Reagendada pela IA:" sem carimbo é dela inteiro; sem carimbo, só o
// "Valor da consulta: R$ 150" fixo que abre a observação é da IA.
const VALUE_PATTERN = /valor\s*:\s*(?:R\$\s*)?([\d.]+(?:,\d{1,2})?)/i;
const REAL_PATTERN = /R\$\s*([\d.]+(?:,\d{1,2})?)/i;
const AI_FIXED_VALUE_LINE = /^\s*Valor da consulta:\s*R\$\s*150(?:,00)?\s*$/i;
const AI_STAMP_LINE = /agendado pela IA\s*$/i;
const AI_RESCHEDULE_LINE = /^\s*Reagendada pela IA:/i;
export const humanNotes = text =>
  String(text ?? '')
    .split(/\n[ \t]*\n/)
    .flatMap((paragraph, index) => {
      const lines = paragraph.split('\n');
      let stamp = -1;
      lines.forEach((line, i) => {
        if (AI_STAMP_LINE.test(line)) stamp = i;
      });
      let aiUntil = -1;
      if (stamp >= 0) aiUntil = stamp;
      else if (AI_RESCHEDULE_LINE.test(lines[0] || ''))
        aiUntil = lines.length - 1;
      else if (index === 0 && AI_FIXED_VALUE_LINE.test(lines[0] || ''))
        aiUntil = 0;
      return lines.filter((_line, i) => i > aiUntil);
    })
    .join('\n');
// o valor que VALE para o agendamento: { text, source, label }
export const effectiveAppointmentPrice = (prices, appt) => {
  const manual = (appt.charges || []).filter(c =>
    String(c.amount || '').trim()
  );
  if (manual.length)
    return {
      text: sumPrices(manual.map(c => c.amount)),
      source: 'manual',
      label: 'lançado à mão neste card',
    };
  const notes = humanNotes(appt.description);
  const explicit = VALUE_PATTERN.exec(notes);
  if (explicit)
    return {
      text: sumPrices([explicit[1]]),
      source: 'observacao',
      label: 'escrito na observação',
    };
  const written =
    VALUE_PATTERN.exec(appt.procedure || '') ||
    REAL_PATTERN.exec(appt.procedure || '');
  if (written)
    return {
      text: sumPrices([written[1]]),
      source: 'procedimento',
      label: 'escrito no tipo de consulta',
    };
  const surgery = appt.kind === 'cirurgias';
  const preset = surgery ? null : presetAppointmentPrice(prices, appt);
  if (preset)
    return {
      ...preset,
      source: 'preset',
      label: `pré-configurado · ${preset.label}`,
    };
  const loose = REAL_PATTERN.exec(notes);
  if (loose)
    return {
      text: sumPrices([loose[1]]),
      source: 'observacao',
      label: 'escrito na observação',
    };
  if (surgery) return null;
  return {
    text: '',
    source: 'padrao',
    label: 'valor padrão do lembrete de confirmação',
  };
};
